import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
    Box,
    Button,
    Flex,
    HStack,
    Input,
    Skeleton,
    Stack,
    Text,
} from '@chakra-ui/react'
import { LuChevronRight, LuSearch } from 'react-icons/lu'
import { PageHeader } from '../components/ui/PageHeader'
import { toaster } from '../components/ui/toaster'
import { UserBadges } from '../components/users/UserBadges'
import { accountLabel, formatAuthDate } from '../utils/userDisplay'
import { listAuthUsers } from '../services/userAdmin'
import { useTranslation } from '../i18n'

const FILTERS = ['staff', 'participants', 'all']

// A page of Auth can hold nothing that passes the filter (a thousand
// anonymous participants, say), so one "Load more" keeps reading until it has
// something to show — up to this many pages, so a click never runs away.
const MAX_PAGES_PER_LOAD = 5

// Worth asking Auth for directly: an email, or something as long as a uid.
// A shorter word is a name, filtered among the rows already loaded.
const isExactQuery = (text) => text.includes('@') || /^[^\s]{20,128}$/.test(text)

function UserRow({ user }) {
    const { t, lang } = useTranslation()
    const label = accountLabel(user, t)
    const lastSignIn = formatAuthDate(user.lastSignInAt, lang)
    return (
        <Flex
            as={RouterLink}
            to={`/users/${user.uid}`}
            align="center"
            p={3}
            gap={3}
            bg="var(--mc-bg-surface)"
            border="1px solid"
            borderColor="var(--mc-border)"
            borderRadius="md"
            _hover={{ bg: 'var(--mc-bg-selected)' }}
            _focusVisible={{ outline: '2px solid var(--mc-info)', outlineOffset: '2px' }}
        >
            <Stack gap={0.5} flex="1" minW={0}>
                <HStack gap={2} wrap="wrap">
                    <Text fontWeight="600" color="var(--mc-text)">{label}</Text>
                    <UserBadges user={user} />
                </HStack>
                <Text fontSize="xs" color="var(--mc-text-muted)" wordBreak="break-all">
                    {user.email && user.displayName ? `${user.email} · ` : ''}
                    <Text as="span" fontFamily="'JetBrains Mono', monospace">{user.uid}</Text>
                </Text>
            </Stack>
            <Stack gap={0} align="flex-end" display={{ base: 'none', md: 'flex' }} flexShrink={0}>
                {user.eventRoleCount > 0 && (
                    <Text fontSize="sm" color="var(--mc-text)">
                        {user.eventRoleCount === 1 ? t('users.eventCountOne') : t('users.eventCount', { count: user.eventRoleCount })}
                    </Text>
                )}
                <Text fontSize="xs" color="var(--mc-text-muted)">
                    {lastSignIn ? t('users.lastSignIn', { date: lastSignIn }) : t('users.neverSignedIn')}
                </Text>
            </Stack>
            <LuChevronRight color="var(--mc-text-muted)" />
        </Flex>
    )
}

export default function UsersPage() {
    const { t } = useTranslation()
    const [filter, setFilter] = useState('staff')
    const [rows, setRows] = useState([])
    const [pageToken, setPageToken] = useState(null)
    const [loading, setLoading] = useState(true)
    const [text, setText] = useState('')
    // Set while the list shows the answer to an exact email/uid search.
    const [lookup, setLookup] = useState(null)

    // Every load and lookup takes a number; only the latest may write. A filter
    // clicked while the previous one is still reading (up to five pages) would
    // otherwise show whichever answer happened to arrive last.
    const latest = useRef(0)

    const loadPages = useCallback(async ({ reset, token }) => {
        const id = ++latest.current
        setLoading(true)
        try {
            const found = []
            let next = token ?? null
            for (let i = 0; i < MAX_PAGES_PER_LOAD; i++) {
                const res = await listAuthUsers({ filter, pageToken: next || undefined })
                if (id !== latest.current) return
                found.push(...res.users)
                next = res.nextPageToken
                if (found.length > 0 || !next) break
            }
            setRows((prev) => (reset ? found : [...prev, ...found]))
            setPageToken(next)
        } catch (err) {
            if (id !== latest.current) return
            toaster.create({ title: t('users.loadFailed', { message: err.message }), type: 'error' })
        } finally {
            if (id === latest.current) setLoading(false)
        }
    }, [filter, t])

    useEffect(() => {
        setLookup(null)
        loadPages({ reset: true })
    }, [loadPages])

    const runLookup = async (query) => {
        const id = ++latest.current
        setLoading(true)
        try {
            const res = await listAuthUsers({ query })
            if (id === latest.current) setLookup({ query, users: res.users })
        } catch (err) {
            if (id === latest.current) {
                toaster.create({ title: t('users.loadFailed', { message: err.message }), type: 'error' })
            }
        } finally {
            if (id === latest.current) setLoading(false)
        }
    }

    const onSubmit = (e) => {
        e.preventDefault()
        const query = text.trim()
        if (query && isExactQuery(query)) runLookup(query)
    }

    const clear = () => {
        setText('')
        setLookup(null)
    }

    const visible = useMemo(() => {
        if (lookup) return lookup.users
        const needle = text.trim().toLowerCase()
        if (!needle || isExactQuery(needle)) return rows
        return rows.filter((u) =>
            [u.displayName, u.email, u.uid].some((v) => v && v.toLowerCase().includes(needle)))
    }, [lookup, rows, text])

    return (
        <Stack gap={4}>
            <PageHeader title={t('users.title')} subtitle={t('users.subtitle')} />

            <Flex gap={3} wrap="wrap" align="center">
                <HStack gap={0} role="group" aria-label={t('users.title')}>
                    {FILTERS.map((f) => (
                        <Button
                            key={f}
                            size="sm"
                            variant={filter === f ? 'solid' : 'outline'}
                            aria-pressed={filter === f}
                            onClick={() => setFilter(f)}
                        >
                            {t(`users.filter${f[0].toUpperCase()}${f.slice(1)}`)}
                        </Button>
                    ))}
                </HStack>
                <Box as="form" onSubmit={onSubmit} flex="1" minW={{ base: '100%', md: '280px' }}>
                    <HStack gap={2}>
                        <Input
                            size="sm"
                            value={text}
                            onChange={(e) => {
                                setText(e.target.value)
                                if (lookup) setLookup(null)
                            }}
                            placeholder={t('users.searchPlaceholder')}
                            aria-label={t('users.searchPlaceholder')}
                        />
                        <Button size="sm" variant="outline" type="submit" disabled={!isExactQuery(text.trim())}>
                            <LuSearch /> {t('users.search')}
                        </Button>
                        {(text || lookup) && (
                            <Button size="sm" variant="ghost" onClick={clear}>{t('users.clearSearch')}</Button>
                        )}
                    </HStack>
                </Box>
            </Flex>

            <Stack gap={2}>
                {visible.map((user) => <UserRow key={user.uid} user={user} />)}
                {loading && [1, 2, 3].map((i) => <Skeleton key={i} height="56px" borderRadius="md" />)}
                {!loading && visible.length === 0 && (
                    <Text color="var(--mc-text-muted)" py={6} textAlign="center">
                        {lookup || text.trim() ? t('users.noMatch', { query: lookup?.query ?? text.trim() }) : t('users.empty')}
                    </Text>
                )}
            </Stack>

            {!lookup && pageToken && !loading && (
                <Button variant="outline" alignSelf="center" onClick={() => loadPages({ reset: false, token: pageToken })}>
                    {t('users.loadMore')}
                </Button>
            )}
        </Stack>
    )
}
